import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Moon, Heartbeat, Lightning } from "@phosphor-icons/react";
import FamilyChallenges from "@/components/FamilyChallenges";

const PERIODS = [[7, "Неделя"], [30, "30 дней"], [180, "180 дней"], [0, "Всё время"]];
const rankStyle = ["text-[#ffd700]", "text-[#c0c0c0]", "text-[#cd7f32]"];

function Board({ title, icon, members, metric, unit, testId }) {
  const ranked = members.filter((m) => m[metric] != null).sort((a, b) => b[metric] - a[metric]);
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {ranked.length === 0 ? (
        <p className="text-xs text-muted-foreground">Нет данных — участники ещё не подключили Whoop.</p>
      ) : (
        <div className="space-y-2">
          {ranked.map((m, i) => (
            <div key={m.id} className="flex items-center gap-2.5" data-testid={`${testId}-row-${i}`}>
              <span className={`w-6 text-center font-heading font-extrabold num ${rankStyle[i] || "text-muted-foreground"}`}>{i + 1}</span>
              <span className="flex-1 text-sm font-semibold truncate">{m.name}</span>
              <span className="text-sm num font-bold">{m[metric]}{unit}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FamilyLeaderboard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/family/leaderboard?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-4" data-testid="family-leaderboard">
      <FamilyChallenges />
      <div className="flex items-center gap-2 flex-wrap">
        {PERIODS.map(([d, label]) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`lb-period-${d}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${days === d ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>
      {loading && !data ? (
        <div className="glass-card h-40 animate-pulse" />
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Не удалось загрузить лидерборд.</p>
      ) : (
        <>
          <div className="glass-card p-5" data-testid="lb-overall">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={18} weight="duotone" className="text-[#ffd700]" />
              <h3 className="text-sm font-semibold">Общий зачёт</h3>
              <span className="text-[10px] text-muted-foreground ml-auto">очки за места в сне, восстановлении и нагрузке</span>
            </div>
            <div className="space-y-2.5">
              {data.members.map((m, i) => (
                <div key={m.id} className="flex items-center gap-3" data-testid={`lb-overall-row-${i}`}>
                  <span className={`w-7 text-center font-heading text-lg font-extrabold num ${rankStyle[i] || "text-muted-foreground"}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{m.name}</div>
                    <div className="text-[11px] text-muted-foreground num">
                      {m.connected ? <>сон {m.sleep_h ?? "—"} ч · восст. {m.recovery ?? "—"}% · нагрузка {m.strain ?? "—"}</> : "Whoop не подключён"}
                    </div>
                  </div>
                  <span className="text-sm num font-bold text-primary">{m.points} очк.</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Board title="Сон" icon={<Moon size={16} weight="duotone" className="text-[#a970ff]" />}
              members={data.members} metric="sleep_h" unit=" ч" testId="lb-sleep" />
            <Board title="Восстановление" icon={<Heartbeat size={16} weight="duotone" className="text-[#00e676]" />}
              members={data.members} metric="recovery" unit="%" testId="lb-recovery" />
            <Board title="Нагрузка" icon={<Lightning size={16} weight="duotone" className="text-[#0084ff]" />}
              members={data.members} metric="strain" unit="" testId="lb-strain" />
          </div>
        </>
      )}
    </div>
  );
}
